import {
  type,
  intersection,
  record,
  string,
  unknown,
  optional,
  object,
} from 'superstruct';
import type { Infer } from 'superstruct';

import { dashboardConfigSchema } from './module-config-schemas/dashboard';
import { coreConfigSchema } from './module-config-schemas/core';
import { assetConfigConfigSchema } from './module-config-schemas/asset-config';
import { benefitsConfigSchema } from './module-config-schemas/benefits';
import { chatHistoryConfigSchema } from './module-config-schemas/chat-history';
import { exampleModuleConfigSchema } from './module-config-schemas/example-module';
import { careCollectionsConfigSchema } from './module-config-schemas/care-collections';
import { careFinderConfigSchema } from './module-config-schemas/care-finder';
import { claimsConfigSchema } from './module-config-schemas/claims';
import { careDiscoveryConfigSchema } from './module-config-schemas/care-discovery';
import { walletConfigSchema } from './module-config-schemas/wallet';
import { liveChatConfigSchema } from './module-config-schemas/live-chat';
import { healthJourneyConfigSchema } from './module-config-schemas/health-journey';
import { careDocumentsConfigSchema } from './module-config-schemas/care-documents';
import { rewardsEventsConfigSchema } from './module-config-schemas/rewards-events';
import { rewardsAchievementsConfigSchema } from './module-config-schemas/rewards-achievements';
import { salesforceLiveChatConfigSchema } from './module-config-schemas/salesforce-live-chat';
import type { ServerConfig } from './module-config-schemas/server-driven';

export const knownLeagueConfigSchema = type({
  core: coreConfigSchema,
  assets: optional(assetConfigConfigSchema),
  benefits: optional(benefitsConfigSchema),
  chatHistory: optional(chatHistoryConfigSchema),
  healthJourney: optional(healthJourneyConfigSchema),
  careCollections: optional(careCollectionsConfigSchema),
  careFinder: optional(careFinderConfigSchema),
  careDiscovery: optional(careDiscoveryConfigSchema),
  wallet: optional(walletConfigSchema),
  claims: optional(claimsConfigSchema),
  liveChat: optional(liveChatConfigSchema),
  salesforceLiveChat: optional(salesforceLiveChatConfigSchema),
  exampleModule: optional(exampleModuleConfigSchema),
  dashboard: optional(dashboardConfigSchema),
  careDocuments: optional(careDocumentsConfigSchema),
  rewardsEvents: optional(rewardsEventsConfigSchema),
  rewardsAchievements: optional(rewardsAchievementsConfigSchema),
  __internal: optional(object()),
});

export type KnownLeagueConfig = Infer<typeof knownLeagueConfigSchema>;

export const unknownLeagueConfigSchema = record(string(), unknown());

export type UnknownLeagueConfig = Infer<typeof unknownLeagueConfigSchema>;

/**
 * A Superstruct schema representing the entire config object. Includes the Core
 * config as a required property, as well as all SDK module configs as optional properties. Finally,
 * allows also for other arbitrary keys, but knows nothing about their types (this
 * will allow anyone to "plug into" the Config API, albeit without automatic
 * runtime validation).
 */
export const leagueConfigSchema = intersection([
  knownLeagueConfigSchema,
  unknownLeagueConfigSchema,
]);

export type LeagueConfig = Infer<typeof leagueConfigSchema>;

export type { ServerConfig };
